import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { AttendanceRecordItem } from "../../types";
import { SearchAttendance } from "../../services/endpoints/attendance.endpoint";
import { logout } from "./authSlice";
import type { RootState } from "../store";

export interface AttendanceState {
  records: AttendanceRecordItem[];
  isLoading: boolean;
  error: string | null;
}

const initialState: AttendanceState = {
  records: [],
  isLoading: false,
  error: null,
};

export const getAttendanceThunk = createAsyncThunk<
  AttendanceRecordItem[],
  string | { query: string; force?: boolean },
  { state: RootState; rejectValue: string }
>(
  "attendance/getAttendance",
  (param, { rejectWithValue }) => {
    const selectQuery = typeof param === "string" ? param : param.query;
    return SearchAttendance({ selectQuery })
      .then((res) => res.data.records)
      .catch((error: any) => rejectWithValue(error.message));
  },
  {
    condition: (param, { getState }) => {
      const { attendance } = getState();
      const force = typeof param === "object" && param.force;
      if (force) return true;
      return attendance.records.length === 0 && !attendance.isLoading && !attendance.error;
    },
  }
);

const attendanceSlice = createSlice({
  name: "attendance",
  initialState,
  reducers: {
    clearAttendance: (state) => {
      state.records = [];
      state.isLoading = false;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getAttendanceThunk.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getAttendanceThunk.fulfilled, (state, action) => {
        state.isLoading = false;
        state.records = action.payload;
        state.error = null;
      })
      .addCase(getAttendanceThunk.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || null;
      })
      .addCase(logout, (state) => {
        state.records = [];
        state.isLoading = false;
        state.error = null;
      });
  },
});

export const { clearAttendance } = attendanceSlice.actions;
export default attendanceSlice.reducer;
